import {
  prepareProductImage,
  productImageDataUrl,
  productImageSource,
} from '../../../helpers/product-image'
import { ProductCustomField } from '../../../types/product-custom-fields'
import {
  normalizeCustomFieldIdentity,
  trimCustomFieldText,
} from '../../../helpers/custom-fields'
import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  ViewChild,
  ElementRef,
  signal,
  effect,
  Input,
  OnChanges,
  SimpleChanges,
  SecurityContext,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete'
import { CommonModule, DOCUMENT } from '@angular/common'
import {
  ReactiveFormsModule,
  FormBuilder,
  FormsModule,
  Validators,
  FormControl,
  FormArray,
  FormGroup,
  AbstractControl,
} from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import {
  CanadianCategory,
  ProductOverviewFormData,
} from '../../../types/products'
import { MatIcon } from '@angular/material/icon'
import { MatChipsModule } from '@angular/material/chips'
import { MatButtonModule } from '@angular/material/button'
import { MatSelectModule } from '@angular/material/select'
import { MEASUREMENT_TYPES } from '../../../constants/measurement-types'
import { ROUTES } from '../../../constants/routes'
import { RouteService } from '../../../services/route.service'
import { ProductOverviewForm } from '../../../types/products'
import { Dialog } from '@angular/cdk/dialog'
import { DialogService } from '../../../services/dialog.service'
import { TranslationService } from '../../../services/translation.service'
import { Observable } from 'rxjs'
import { debounceTime, startWith, switchMap } from 'rxjs/operators'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { ProductService } from '../../../services/product.service'
import { MatTooltipModule } from '@angular/material/tooltip'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { MARKETPLACE_CATEGORIES } from '../../../constants/marketplace-categories'
import { currencyOptions } from '../../../constants/currencies'
import { PRODUCT_CONDITIONS } from '../../../constants/product-conditions'

// Maximum length for title abbreviation
const MAX_LENGTH = 40
@Component({
  selector: 'app-product-overview-form',
  imports: [
    MatFormField,
    MatLabel,
    MatInputModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatIcon,
    MatChipsModule,
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    MatAutocompleteModule,
    CommonModule,
    MatTooltipModule,
  ],
  templateUrl: './product-overview-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-overview-form.component.scss',
})
export class ProductOverviewFormComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private routeService = inject(RouteService)
  private productService = inject(ProductService)
  translationService = inject(TranslationService)
  private document = inject<Document>(DOCUMENT)
  private sanitizer = inject(DomSanitizer)

  @Input() clonedData: ProductOverviewFormData | undefined
  @Input() editing: boolean = false
  @Output() formChanged = new EventEmitter<ProductOverviewForm>()

  @ViewChild('pictureFile') pictureFile?: ElementRef
  @ViewChild('safetyFile') safetyFile?: ElementRef
  @ViewChild('recyclingFile') recyclingFile?: ElementRef

  public measurementTypes = MEASUREMENT_TYPES
  public marketplaceCategories = MARKETPLACE_CATEGORIES
  public currencyOptions = currencyOptions
  public productConditions = PRODUCT_CONDITIONS
  public requestUnits = [
    'each',
    'pack',
    'box',
    'set',
    'pair',
    'case',
    'kg',
    'litre',
  ]
  public displayRequestUnit = (unit: string | null): string =>
    unit ? this.translationService.getMarketplaceRequestUnitValueText(unit) : ''
  public productImageBase64 = ''
  pictureProcessing = signal(false)

  // Abbreviate title if it exceeds max length
  public abbreviate(title: string) {
    return title.length > MAX_LENGTH
      ? `${title.slice(0, MAX_LENGTH)}...`
      : title
  }

  categoryCtrl = new FormControl('')
  selectedCategories = signal<CanadianCategory[]>([])

  filteredCategories: Observable<CanadianCategory[]> = new Observable<
    CanadianCategory[]
  >()

  @ViewChild('categoryInput') categoryInput!: ElementRef<HTMLInputElement>

  private savedCustomFieldNames = new WeakMap<FormControl<string>, string>()

  customFields = new FormArray<
    FormGroup<{
      name: FormControl<string>
      type: FormControl<ProductCustomField['type']>
      unit: FormControl<string>
      value: FormControl<string | number | boolean | null>
    }>
  >([], (control: AbstractControl) => {
    const fields = control.value as ProductCustomField[]
    const names = fields.map((field) =>
      normalizeCustomFieldIdentity(field.name),
    )
    if (new Set(names).size !== names.length) return { duplicate: true }
    // Match Go's JSON encoding, including omitted empty units and escaped text.
    const encoded = JSON.stringify(
      fields.map(({ unit, ...field }) => (unit ? { ...field, unit } : field)),
    ).replace(
      /[<>&\u2028\u2029]/g,
      (character) =>
        `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`,
    )
    if (fields.length > 50 || new TextEncoder().encode(encoded).length > 65535)
      return { limit: true }
    return null
  })

  /**
   * Uses the entered field name as its label, or a numbered fallback while the name is empty.
   * @param {number} index Position of the field in the form.
   * @returns {string} Entered field name or a numbered fallback label.
   */
  customFieldLabel(index: number): string {
    return (
      trimCustomFieldText(this.customFields.at(index).controls.name.value) ||
      `${this.translationService.getCustomFieldNameText()} ${index + 1}`
    )
  }

  /**
   * Capitalizes a name only when its API identity and byte limit stay unchanged.
   * @param {string} name Entered field name.
   * @returns {string} Trimmed name with safe capitalization.
   */
  private capitalizeCustomFieldName(name: string): string {
    const trimmed = trimCustomFieldText(name)
    const first = Array.from(trimmed)[0] ?? ''
    const capitalized = first.toUpperCase() + trimmed.slice(first.length)
    if (
      normalizeCustomFieldIdentity(capitalized) !==
        normalizeCustomFieldIdentity(trimmed) ||
      new TextEncoder().encode(capitalized).length > 128
    )
      return trimmed
    return capitalized
  }

  /**
   * Normalizes an edited field name while preserving names loaded from the API.
   * @param {number} index Position of the field in the form.
   * @returns {void} No return value.
   */
  normalizeCustomFieldName(index: number): void {
    const control = this.customFields.at(index).controls.name
    // Loaded names are already normalized by the API, including legacy exceptions.
    if (control.value === this.savedCustomFieldNames.get(control)) return
    control.setValue(this.capitalizeCustomFieldName(control.value))
  }

  /**
   * Adds a field row with validation for its selected type. Changing type clears the value and unit.
   * @param {ProductCustomField} [field] Saved field to load when editing or cloning a product.
   * @returns {void} No return value.
   */
  addCustomField(field?: ProductCustomField): void {
    if (this.customFields.length >= 50) return
    const row = new FormGroup({
      name: new FormControl(field?.name ?? '', {
        nonNullable: true,
        validators: (c) =>
          trimCustomFieldText(c.value) &&
          new TextEncoder().encode(c.value).length <= 128
            ? null
            : { name: true },
      }),
      type: new FormControl<ProductCustomField['type']>(field?.type ?? 'text', {
        nonNullable: true,
      }),
      unit: new FormControl(field?.unit ?? '', {
        nonNullable: true,
        validators: (c) =>
          new TextEncoder().encode(c.value).length <= 64
            ? null
            : { unit: true },
      }),
      value: new FormControl<string | number | boolean | null>(
        field?.value ?? null,
      ),
    })
    row.controls.value.setValidators((control) => {
      const value = control.value
      switch (row.controls.type.value) {
        case 'text':
          return typeof value === 'string' &&
            trimCustomFieldText(value) &&
            new TextEncoder().encode(value).length <= 2048
            ? null
            : { value: true }
        case 'number':
          return typeof value === 'number' && Number.isFinite(value)
            ? null
            : { value: true }
        case 'boolean':
          return typeof value === 'boolean' ? null : { value: true }
        default:
          return { value: true }
      }
    })
    row.controls.type.valueChanges.subscribe(() => {
      row.controls.value.reset(null)
      row.controls.unit.setValue('')
    })
    row.controls.value.updateValueAndValidity()
    if (field) this.savedCustomFieldNames.set(row.controls.name, field.name)
    this.customFields.push(row)
  }

  /**
   * Removes a custom field row from the product form.
   * @param {number} index Position of the field to remove.
   * @returns {void} No return value.
   */
  removeCustomField(index: number): void {
    this.customFields.removeAt(index)
  }

  overviewForm = this.fb.group({
    customFields: this.customFields,
    name: ['', Validators.required],
    isMarketplacePrivate: [false],
    allowRequests: [false],
    marketplaceCategory: [null as string | null],
    marketplaceSubcategory: [null as string | null],
    requestUnit: ['each', Validators.required],
    priceAmount: [
      null as number | null,
      (control: AbstractControl) =>
        control.value == null ||
        control.value === '' ||
        Number(control.value) > 0
          ? null
          : { positive: true },
    ],
    priceCurrency: [null as string | null, Validators.pattern(/^[A-Za-z]{3}$/)],
    condition: [null as string | null],
    droneFlightTimeMinutes: [
      null as number | null,
      (control: AbstractControl) =>
        control.value == null ||
        control.value === '' ||
        Number(control.value) > 0
          ? null
          : { positive: true },
    ],
    droneType: [null as string | null],
    droneVideoResolution: [null as string | null],
    droneSensorSize: [null as string | null],
    droneFpv: [null as boolean | null],
    articleCategory: [''],
    type: [''],
    primaryArticleIdentifier: ['', Validators.required],
    otherNames: [''],
    measureUnit: [''],
    measureValue: [
      0,
      [Validators.pattern(/^[+]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/)],
    ],
    gs1Identifier: [''],
    sku: ['', Validators.required],
    batch: [''],
    batch_upper: [''],
    serial: [''],
    serial_upper: [''],
    diameter: [''],
    width: [''],
    height: [''],
    picture: [''],
    brand: ['', Validators.required],
    categoryCode: [''],
    codeDescription: [''],
    barcodeIdentifier: [''],
    productionInCanada: [
      false,
      [Validators.required, this.productionInCanadaFormControl],
    ],
    description: [''],
    safeUseInstructions: [''],
    safetyDataSheet: [''],
    recyclingInstructions: [''],
    disassemblingInstructions: [''],
    englishShortName: [''],
    frenchShortName: [''],
  })
  window: Window

  constructor() {
    this.window = this.document.defaultView as Window
    this.filteredCategories = this.categoryCtrl.valueChanges.pipe(
      debounceTime(300),
      startWith(null),
      switchMap((value) => {
        if (value == null || value.length < 2) {
          return []
        }
        return this.filter(value)
      }),
    )

    effect(() => {
      /*
        Whenever the selected categories change, this code fills the category code
        field with the appropriate hs code
      */
      this.overviewForm.patchValue({
        categoryCode: this.selectedCategories().reduce(
          (sum: string, current: CanadianCategory, index, arr) => {
            if (index < arr.length - 1) {
              return `${sum} ${current.code}, `
            } else {
              return `${sum} ${current.code}`
            }
          },
          '',
        ),
      })
    })
  }

  private updateNonchemicalRequirements(): void {
    const nonchemical = this.marketplaceCategories.some(
      (category) =>
        category.name === this.overviewForm.value.marketplaceCategory,
    )
    for (const key of ['brand', 'sku'] as const) {
      const control = this.overviewForm.controls[key]
      if (nonchemical) control.removeValidators(Validators.required)
      else control.addValidators(Validators.required)
      control.updateValueAndValidity({ emitEvent: false })
    }
  }

  async ngOnInit() {
    this.updateNonchemicalRequirements()
    this.overviewForm.controls.marketplaceCategory.valueChanges.subscribe(() =>
      this.updateNonchemicalRequirements(),
    )
    if (!this.editing) {
      this.overviewForm.controls.priceAmount.addValidators(Validators.required)
      this.overviewForm.controls.priceCurrency.addValidators(
        Validators.required,
      )
      this.overviewForm.controls.priceAmount.updateValueAndValidity()
      this.overviewForm.controls.priceCurrency.updateValueAndValidity()
      this.overviewForm
        .get('marketplaceCategory')
        ?.addValidators(Validators.required)
      this.overviewForm.get('marketplaceCategory')?.updateValueAndValidity()
    }
    this.overviewForm.get('priceAmount')?.valueChanges.subscribe((price) => {
      const currency = this.overviewForm.get('priceCurrency')
      currency?.setValidators(
        !this.editing || price != null
          ? [Validators.required, Validators.pattern(/^[A-Za-z]{3}$/)]
          : [],
      )
      currency?.updateValueAndValidity({ emitEvent: false })
    })
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.COMPANY_PRODUCTS.title,
      `/admin/companies/${ROUTES.COMPANY_PRODUCTS.path}`,
    )
    this.routeService.setRoute(currentRoute)
  }

  get marketplaceSubcategories(): readonly string[] {
    return (
      this.marketplaceCategories.find(
        (category) =>
          category.name === this.overviewForm.value.marketplaceCategory,
      )?.subcategories ?? []
    )
  }

  onMarketplaceCategoryChange(): void {
    this.overviewForm.patchValue({ marketplaceSubcategory: null })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clonedData'] && changes['clonedData'].currentValue) {
      const clonedData = changes['clonedData'].currentValue
      const { customFields: savedFields, ...overview } = clonedData
      this.customFields.clear()
      for (const field of savedFields ?? []) this.addCustomField(field)
      this.overviewForm.patchValue({
        ...overview,
        condition:
          this.productConditions.find(
            (condition) => condition === clonedData.condition,
          ) ?? null,
      })
      this.productImageBase64 = productImageSource(clonedData.picture)

      this.overviewForm
        .get('productionInCanada')
        ?.setValue(clonedData.productionInCanada?.toString() ?? 'null')

      if (clonedData.canadianCategories) {
        this.selectedCategories.set(clonedData.canadianCategories)
      }
    }
  }

  /**
   * Ensures the user must interact with the production in canada field before going forward
   * @param control
   */
  productionInCanadaFormControl(control: AbstractControl) {
    return control.value === false && control.pristine
      ? { mustChange: true }
      : null
  }

  /**
   * Removes a category from the list of selected categories.
   * @param {CanadianCategory} category - The category to be removed.
   */
  removeCategory(category: CanadianCategory): void {
    this.selectedCategories.update((categories) =>
      categories.filter((c) => c.id !== category.id),
    )
  }

  /**
   * Handles the selection of a category from the autocomplete.
   * If the selected category is not already in the list, it adds it and clears the input.
   * @param {MatAutocompleteSelectedEvent} event - The selection event from the autocomplete.
   */
  selected(event: MatAutocompleteSelectedEvent): void {
    const category = event.option.value
    if (category) {
      const categoryAlreadySelected = this.selectedCategories().some(
        (exisingCategory: CanadianCategory) => {
          return exisingCategory.id == category.id
        },
      )
      if (!categoryAlreadySelected) {
        this.selectedCategories.update((categories) => [
          ...categories,
          category,
        ])
        // Clear the input value
        this.categoryInput.nativeElement.value = ''
        this.categoryCtrl.setValue(null)
      }
    }
  }

  /**
   * Filters the list of Canadian categories based on the input value.
   * It fetches categories from the server, filters out already selected ones,
   * and processes the results.
   * @param {string} value - The input value to filter categories.
   * @returns {Promise<CanadianCategory[]>} A promise that resolves to an array of filtered categories.
   *
   */
  private async filter(value: string): Promise<CanadianCategory[]> {
    try {
      const filterValue = value.toLowerCase()
      const response = await this.productService
        .getCanadianCategories(filterValue)
        .toPromise()
      if (response == null) {
        return []
      }
      // Filter out already selected categories
      const results = response.results.filter(
        (category) =>
          !this.selectedCategories().some(
            (selected: CanadianCategory) => selected.id === category.id,
          ),
      )
      const processedResults = this.processCategories(results)
      return processedResults
    } catch (err) {
      console.error('Error fetching product categories:', err)
      return []
    }
  }

  /**
   * Processes an array of product categories, abbreviating their names etc
   *
   * @param {CanadianCategory[]} categories - The array of product categories to process.
   * @returns {CanadianCategory[]} The processed array of product categories.
   */
  processCategories(categories: CanadianCategory[]): CanadianCategory[] {
    return categories.map((item) => {
      item = { ...item }
      item.displayName = this.getFullCategoryName(item)
      item.name = this.removeUnnecessaryCharacters(item.name)
      return item
    })
  }

  /**
   * Removes specific characters (':' and '-') from the given string.
   * @param {string} name - The string to process.
   * @returns {string} The processed string with ':' and '-' removed.
   * @private
   */
  private removeUnnecessaryCharacters(name: string): string {
    return name.replaceAll(':', '').replaceAll('-', '')
  }

  /**
   * Generates a full category representation including its code, name, and parent categories.
   * This method creates a hierarchical HTML representation of the category's lineage,
   * with line breaks, indentation, and arrows after each parent.
   *
   * @param {CanadianCategory} category - The category for which to generate the full representation.
   * @returns {SafeHtml} A SafeHtml object representing the full category, including its code, name, and parent categories.
   *
   * @example
   * // If category has code "1234", parents "Electronics" and "Computers", and its name is "Laptops"
   * // This method might return HTML equivalent to:
   * // <div class="category-option">
   * //   <div class="category-name">
   * //     1234 - Electronics
   * //            -> Computers
   * //               -> Laptops
   * //   </div>
   * // </div>
   *
   * @private
   */
  private getFullCategoryName(category: CanadianCategory): SafeHtml {
    const parents: CanadianCategory[] = []
    let currentCategory = category.parent
    while (currentCategory) {
      parents.unshift(currentCategory)
      currentCategory = currentCategory.parent
    }

    // Sanitize all category data
    const sanitizedCategory = {
      code: this.sanitizer.sanitize(SecurityContext.HTML, category.code) || '',
      name: this.sanitizer.sanitize(SecurityContext.HTML, category.name) || '',
    }

    const sanitizedParents = parents.map((parent) => ({
      name: this.sanitizer.sanitize(SecurityContext.HTML, parent.name) || '',
    }))

    // Process sanitized data
    const categoryName: string = this.abbreviate(
      this.removeUnnecessaryCharacters(sanitizedCategory.name),
    )

    let categoryHierarchy = ''
    const codeLength = sanitizedCategory.code.length
    if (sanitizedParents.length > 0) {
      categoryHierarchy = sanitizedParents
        .filter((_, index) => index > 0)
        .map((parent, index) => {
          const parentName = this.abbreviate(
            this.removeUnnecessaryCharacters(parent.name),
          )
          const indentation = '&nbsp;'.repeat((index + 1) * 4 + codeLength)
          return `${indentation}-&gt; ${parentName}<br>`
        })
        .join('')
    }

    const finalIndentation = '&nbsp;'.repeat(
      (sanitizedParents.length + 1) * 3 + codeLength,
    )
    categoryHierarchy += `${finalIndentation}-&gt; ${categoryName}`

    const formattedOption = `
       <div class="category-option">
         <div class="category-name" style="white-space: pre-wrap;">
 ${sanitizedCategory.code} - ${this.abbreviate(this.removeUnnecessaryCharacters(sanitizedParents[0]?.name || ''))}
 ${categoryHierarchy}
         </div>
       </div>
     `

    return this.sanitizer.bypassSecurityTrustHtml(formattedOption)
  }

  /**
   * Opens the Tariff Finder tool in a new tab.
   *
   */
  public showExternalHSCodeTool(): void {
    this.window.open('https://www.tariffinder.ca/en/getStarted', '_blank')
  }

  /**
   * Resets form data
   */
  onReset() {
    this.customFields.clear()
    this.overviewForm.reset()
    this.categoryCtrl.reset()
    this.selectedCategories.set([])
    this.categoryInput.nativeElement.value = ''
    this.productImageBase64 = ''
    this.pictureFile!.nativeElement.value = ''
    this.safetyFile!.nativeElement.value = ''
    this.recyclingFile!.nativeElement.value = ''
  }

  validateForm() {
    if (this.clonedData != null && !this.editing) {
      const clonedProductIdentifierInvalid =
        this.overviewForm.value.primaryArticleIdentifier !== '' &&
        this.clonedData.primaryArticleIdentifier ===
          this.overviewForm.value.primaryArticleIdentifier

      if (
        this.clonedData.name === this.overviewForm.value.name ||
        clonedProductIdentifierInvalid
      ) {
        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getProductsText(),
          this.translationService.getProductNameIdentifierDifferentText(),
        )
        return false
      }
    }

    return this.overviewForm.valid
  }

  /**
   * Prepares the picture and emits the validated overview for the next step.
   * @returns A promise that resolves after validation and photo preparation.
   */
  async onSubmit(): Promise<void> {
    if (this.pictureProcessing()) return
    this.customFields.controls.forEach((_, index) =>
      this.normalizeCustomFieldName(index),
    )
    this.overviewForm.markAllAsTouched()

    if (!this.overviewForm.valid || !this.validateForm()) {
      return
    }
    const data = this.overviewForm.value as ProductOverviewFormData

    data.canadianCategories = this.selectedCategories()
    data.measureValue = parseFloat(`${data.measureValue}`)
    data.priceAmount =
      data.priceAmount === null || `${data.priceAmount}` === ''
        ? null
        : Number(data.priceAmount)
    data.priceCurrency =
      data.priceAmount === null
        ? null
        : (data.priceCurrency?.toUpperCase() ?? null)
    data.droneFlightTimeMinutes =
      data.droneFlightTimeMinutes === null ||
      `${data.droneFlightTimeMinutes}` === ''
        ? null
        : Number(data.droneFlightTimeMinutes)
    const productionInCanada = String(data.productionInCanada)
    data.productionInCanada =
      productionInCanada === 'null' ? null : productionInCanada === 'true'
    data.picture = productImageSource(data.picture)
    if (data.picture) {
      this.pictureProcessing.set(true)
      try {
        // Existing edit/clone pictures may predate the upload limits too.
        data.picture = await productImageDataUrl(
          await prepareProductImage(data.picture),
        )
      } catch (error) {
        this.showPictureError(error)
        return
      } finally {
        this.pictureProcessing.set(false)
      }
    }

    this.formChanged.emit({
      formName: 'overviewForm',
      ...data,
    })
  }

  /**
   * Prepares photos before upload and reads supporting documents as data URIs.
   * Keeps the accepted attachment if the selection is cancelled or invalid.
   * @param event File input change event.
   * @param fieldKey Form control that receives the selected attachment.
   * @returns A promise that resolves after the selection is handled.
   */
  async handleFileSelection(event: Event, fieldKey: string): Promise<void> {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file || file.size === 0) return

    if (fieldKey !== 'picture') {
      const reader = new FileReader()
      reader.onload = () => {
        if (input.files?.[0] === file)
          this.overviewForm.patchValue({ [fieldKey]: reader.result })
      }
      reader.readAsDataURL(file)
      return
    }

    this.pictureProcessing.set(true)
    try {
      const source = await productImageDataUrl(await prepareProductImage(file))
      if (input.files?.[0] !== file) return
      this.productImageBase64 = source
      this.overviewForm.patchValue({ picture: source })
    } catch (error) {
      if (input.files?.[0] !== file) return
      input.value = ''
      // Keep the accepted picture if its replacement cannot be prepared.
      this.showPictureError(error)
    } finally {
      this.pictureProcessing.set(false)
    }
  }

  /**
   * Shows the translated dialog for a photo preparation failure.
   * @param error Preparation error; size failures have their own guidance.
   * @returns Nothing; opens the photo validation dialog.
   */
  private showPictureError(error: unknown): void {
    this.dialogService.showDialog(
      this.dialog,
      this.translationService.getProductsText(),
      error instanceof Error && error.message === 'size'
        ? this.translationService.getImageTooLargeText()
        : this.translationService.getInvalidProductImageText(),
    )
  }
}
